import React from 'react';
import { motion } from 'framer-motion';
import { FlameIcon, SparklesIcon } from 'lucide-react';
interface EbookMockupProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}
export function EbookMockup({
  size = 'md',
  className = ''
}: EbookMockupProps) {
  const sizeClasses = {
    sm: 'w-40 h-56',
    md: 'w-56 h-80',
    lg: 'w-72 h-[26rem]'
  };
  const titleSizes = {
    sm: 'text-2xl',
    md: 'text-4xl',
    lg: 'text-5xl'
  };
  const subtitleSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };
  return <div className={`relative flex items-center justify-center ${className}`} data-id="element-243">
      {/* Floating decorative elements */}
      <motion.div animate={{
      y: [0, -10, 0],
      rotate: [0, 5, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute -top-6 -left-6 text-brand-gold opacity-60" data-id="element-244">
        <SparklesIcon className="w-8 h-8" data-id="element-245" />
      </motion.div>
      <motion.div animate={{
      y: [0, 15, 0],
      rotate: [0, -10, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute -bottom-8 -right-4 text-brand-rose opacity-40" data-id="element-246">
        <FlameIcon className="w-10 h-10" data-id="element-247" />
      </motion.div>

      {/* 3D Book Container */}
      <motion.div initial={{
      rotateY: -15,
      rotateX: 5
    }} animate={{
      rotateY: [-15, -5, -15],
      rotateX: [5, 2, 5]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} style={{
      perspective: '1000px',
      transformStyle: 'preserve-3d'
    }} className={`relative ${sizeClasses[size]} shadow-2xl rounded-r-lg rounded-l-sm`} data-id="element-248">
        {/* Book Spine (Left Edge) */}
        <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-b from-brand-rose to-[#7A3A52] rounded-l-sm border-r border-white/20" style={{
        transform: 'translateZ(-2px) translateX(-2px) rotateY(-90deg)',
        transformOrigin: 'left'
      }} data-id="element-249" />

        {/* Book Pages (Right Edge) */}
        <div className="absolute right-0 top-1 bottom-1 w-6 bg-white rounded-r-sm border-y border-r border-gray-200" style={{
        transform: 'translateZ(-10px) translateX(3px) rotateY(90deg)',
        transformOrigin: 'right'
      }} data-id="element-250">
          {/* Page lines simulation */}
          <div className="w-full h-full flex flex-col justify-evenly opacity-20" data-id="element-251">
            {[...Array(20)].map((_, i) => <div key={i} className="w-full h-[1px] bg-gray-400" data-id="element-252" />)}
          </div>
        </div>

        {/* Front Cover */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-rose via-[#B85D7F] to-brand-gold rounded-r-lg rounded-l-sm overflow-hidden border border-white/20 flex flex-col items-center justify-center p-6 text-center" style={{
        transform: 'translateZ(0)'
      }} data-id="element-253">
          {/* Cover Design */}
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-254" />

          <div className="relative z-10 flex flex-col items-center h-full justify-between py-4" data-id="element-255">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 border border-white/30" data-id="element-256">
              <FlameIcon className="w-6 h-6 text-white" data-id="element-257" />
            </div>

            <div className="flex-1 flex flex-col justify-center" data-id="element-258">
              <h3 className={`font-serif font-bold text-white leading-tight mb-2 drop-shadow-md ${titleSizes[size]}`} data-id="element-259">
                Acenda
              </h3>
              <div className="w-12 h-1 bg-brand-gold mx-auto rounded-full mb-4 opacity-80" data-id="element-260" />
              <p className={`text-white/90 font-medium italic ${subtitleSizes[size]}`} data-id="element-261">
                O guia definitivo
              </p>
            </div>

            <p className={`text-white/70 font-serif tracking-widest uppercase mt-auto ${size === 'sm' ? 'text-[8px]' : 'text-xs'}`} data-id="element-262">
              Velas Essenciais
            </p>
          </div>
        </div>
      </motion.div>
    </div>;
}